/**
 * <ziva-scent-story>: the pinned fragrance story.
 *
 * The scene is sticky in CSS and the section's height is the scroll budget;
 * every fragrance gets an equal share of it. The scroll only decides which
 * fragrance is on stage. The change itself is a timed transition that always
 * plays through: the sky dissolves, the old bottle settles back and fades
 * while the new one comes to rest, the copy arrives line by line and a shine
 * crosses the glass. Scrubbed transitions sat half-faded under a slow finger
 * and jumped under a fast one; a timed one looks the same at any speed.
 *
 * What stays tied to the scroll is a slow, smoothed drift (the bottle and
 * the sky move a few pixels), so the stage never feels frozen.
 *
 * Everything is set up in connectedCallback and reverted in
 * disconnectedCallback, which is also what the theme editor triggers when it
 * re-renders or removes the section. GSAP and ScrollTrigger are loaded by the
 * section as deferred classic scripts; this module runs after them.
 */

/* A scene changes once the scroll is this far (in scenes) past the boundary,
   so a finger resting on the line does not flick back and forth. */
const SWITCH_MARGIN = 0.06;

class ZivaScentStory extends HTMLElement {
  connectedCallback() {
    this.cancelled = false;
    this.loadMedia();
    this.setupAnimation();
  }

  disconnectedCallback() {
    this.cancelled = true;
    this.bottleObserver?.disconnect();
    cancelAnimationFrame(this.refreshFrame);
    this.media?.revert();
    this.media = undefined;
  }

  query(selector) {
    return Array.from(this.querySelectorAll(selector));
  }

  /* Nothing here loads until the section is two screens away. Then the CSS
     images (grain, engravings, shine masks) are let through with
     data-media-ready, and the off-screen bottles decode one at a time:
     loading all four at once rasterised four full-size images in the same
     frame on entering. */
  loadMedia() {
    const layers = this.query('[data-scent-layer]').filter((layer) =>
      layer.querySelector('img[data-deferred-src]'),
    );

    const loadSequentially = async () => {
      for (const layer of layers) {
        if (this.cancelled) return;
        const image = layer.querySelector('img[data-deferred-src]');
        const source = layer.querySelector('source[data-deferred-srcset]');
        if (!image || image.getAttribute('src')) continue;
        if (source) source.srcset = source.dataset.deferredSrcset;
        image.src = image.dataset.deferredSrc;
        await image.decode().catch(() => undefined);
      }
      // The bottles resolve after the trigger measured the section.
      if (!this.cancelled) window.ScrollTrigger?.refresh();
    };

    this.bottleObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.bottleObserver.disconnect();
        this.dataset.mediaReady = '';
        if (layers.length) loadSequentially();
      },
      {rootMargin: '200% 0px'},
    );
    this.bottleObserver.observe(this);
  }

  setupAnimation() {
    const {gsap, ScrollTrigger} = window;
    const atmosphere = this.querySelector('.ziva-scent-story__atmosphere');
    const skies = this.query('[data-scent-sky]');
    const layers = this.query('[data-scent-layer]');
    // The bottle with its floor shadow; both drift together.
    const bottleStages = layers.map((layer) => layer.querySelector('[data-scent-drift]')).filter(Boolean);
    const copies = this.query('[data-scent-copy]');
    const copyLines = copies.map((copy) => Array.from(copy.children));
    const sweeps = this.query('[data-scent-sweep]');
    const liveRegion = this.querySelector('[data-scent-live]');
    if (!gsap || !ScrollTrigger || skies.length < 2 || layers.length < 2) return;

    gsap.registerPlugin(ScrollTrigger);
    // Mobile browsers fire resize when the URL bar collapses; without this
    // every toggle re-measures the section and jumps the scroll.
    ScrollTrigger.config({ignoreMobileResize: true});

    const scenes = copies.map((copy) => ({
      label: copy.querySelector('.ziva-scent-story__product-name')?.textContent.trim() ?? '',
      headline: copy.querySelector('h3')?.textContent.trim() ?? '',
    }));
    const last = scenes.length - 1;

    let announced = -1;
    const updateAccessibility = (index) => {
      if (index !== 'all' && index === announced) return;
      announced = index === 'all' ? -1 : index;
      this.dataset.activeScene = index === 'all' ? 'all' : String(index);
      layers.forEach((layer, i) => {
        layer.setAttribute('aria-hidden', index === 'all' || i === index ? 'false' : 'true');
      });
      copies.forEach((copy, i) => {
        const isActive = index === 'all' || i === index;
        copy.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        copy.querySelectorAll('a, button').forEach((control) => {
          control.setAttribute('tabindex', isActive ? '0' : '-1');
        });
      });
      if (index !== 'all' && liveRegion) {
        liveRegion.textContent = `${scenes[index].label}: ${scenes[index].headline}`;
      }
    };

    /* The idle drift on the box engravings: the motifs sit inside the bottle
       layer, so a scene change already shows and hides them. Each moves the
       way its subject would. The loops only run while the section is on
       screen. */
    const driftMotifs = () => {
      const motifs = this.query('[data-scent-motif]');
      if (!motifs.length) return () => {};

      const tweens = motifs.map((motif, index) => {
        const right = motif.dataset.motifSide === 'right';
        // Offset phases so a pair never moves in lockstep.
        const delay = (index % 4) * 0.55;

        switch (motif.dataset.motifMotion) {
          case 'run':
            // Shift the repeating mask rather than the element, so the strip
            // stays in its frame and the tile height keeps the loop seamless.
            return gsap.fromTo(
              motif,
              {'--motif-run': '0px'},
              {'--motif-run': right ? '-42px' : '42px', duration: 9, ease: 'none', repeat: -1, delay},
            );
          case 'parallax':
            return gsap.to(motif, {y: right ? 16 : -16, duration: 7.5, ease: 'sine.inOut', repeat: -1, yoyo: true, delay});
          case 'sway': {
            const centred = motif.dataset.motifSide === 'center';
            gsap.set(motif, {transformOrigin: centred ? 'center top' : right ? 'right top' : 'left top'});
            // Small on purpose: a tall column swings its box sideways.
            return gsap.to(motif, {rotation: right ? -1.1 : 1.1, duration: 6, ease: 'sine.inOut', repeat: -1, yoyo: true, delay});
          }
          case 'float':
          default:
            return gsap.to(motif, {
              y: right ? 14 : -14,
              x: right ? -6 : 6,
              rotation: right ? -1.6 : 1.6,
              duration: 6.5,
              ease: 'sine.inOut',
              repeat: -1,
              yoyo: true,
              delay,
            });
        }
      });

      const setPlaying = (playing) => tweens.forEach((tween) => (playing ? tween.play() : tween.pause()));
      setPlaying(false);
      const visibility = ScrollTrigger.create({
        trigger: this,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: ({isActive}) => setPlaying(isActive),
      });
      setPlaying(visibility.isActive);

      return () => {
        visibility.kill();
        tweens.forEach((tween) => tween.kill());
      };
    };

    // matchMedia scopes and reverts on its own, and re-runs on a change.
    this.media = gsap.matchMedia(this);

    this.media.add(
      {
        phone: '(max-width: 900px)',
        motion: '(prefers-reduced-motion: no-preference)',
      },
      (context) => {
        const {phone, motion} = context.conditions;

        if (!motion) {
          gsap.set([...skies, ...layers, ...copies, ...copyLines.flat(), ...sweeps], {clearProps: 'all'});
          updateAccessibility('all');
          return undefined;
        }

        // Distances are smaller on a phone, where the stage is too.
        const lift = phone ? 26 : 40;
        const settle = phone ? 20 : 30;
        const rise = phone ? 16 : 20;

        let current = 0;
        let transition = null;
        let ready = false;

        // Show one scene at once, with no transition: on load and on
        // arriving mid-story (a reload halfway down the page).
        const show = (index) => {
          current = index;
          gsap.set(skies, {autoAlpha: 0});
          gsap.set(skies[index], {autoAlpha: 1});
          gsap.set(layers, {autoAlpha: 0, scale: 1, y: 0});
          gsap.set(layers[index], {autoAlpha: 1});
          gsap.set(copies, {autoAlpha: 0, y: 0});
          gsap.set(copies[index], {autoAlpha: 1});
          gsap.set(copyLines.flat(), {autoAlpha: 1, y: 0});
          gsap.set(sweeps, {autoAlpha: 0, xPercent: -150});
          updateAccessibility(index);
        };

        const isHidden = (element) => Number(gsap.getProperty(element, 'opacity')) < 0.05;
        const others = (list, index) => list.filter((_, i) => i !== index);

        // Animations made later, from scroll callbacks, are still recorded
        // in this context and reverted with it.
        context.add('goTo', (next) => {
          if (next === current) return;
          const direction = next > current ? 1 : -1;
          current = next;
          transition?.kill();
          updateAccessibility(next);

          const layer = layers[next];
          const copy = copies[next];
          const layerHidden = isHidden(layer);
          const copyHidden = isHidden(copy);

          transition = gsap.timeline({defaults: {overwrite: 'auto'}});

          // The sky: a slow dissolve from one palette into the next.
          transition
            .to(others(skies, next), {autoAlpha: 0, duration: 1.1, ease: 'sine.inOut'}, 0)
            .to(skies[next], {autoAlpha: 1, duration: 1.1, ease: 'sine.inOut'}, 0);

          // The bottle on stage steps back and fades…
          transition.to(
            others(layers, next),
            {autoAlpha: 0, scale: 0.94, y: -direction * lift, duration: 0.55, ease: 'power2.in'},
            0,
          );
          // …while the next comes to rest, overlapping so the stage is never
          // empty. From where it was, if it was still leaving.
          if (layerHidden) transition.set(layer, {scale: 1.06, y: direction * settle * 1.5}, 0);
          transition.to(layer, {autoAlpha: 1, scale: 1, y: 0, duration: 1, ease: 'expo.out'}, layerHidden ? 0.2 : 0);

          // The copy: out as one piece, in line by line.
          transition.to(
            others(copies, next),
            {autoAlpha: 0, y: -direction * 12, duration: 0.3, ease: 'power1.in'},
            0,
          );
          if (copyHidden) transition.set(copyLines[next], {autoAlpha: 0, y: direction * rise}, 0);
          transition
            .to(copy, {autoAlpha: 1, y: 0, duration: 0.01}, copyHidden ? 0.3 : 0)
            .to(
              copyLines[next],
              {autoAlpha: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.07},
              copyHidden ? 0.32 : 0,
            );

          // A shine across the glass once it has settled.
          transition
            .set(others(sweeps, next), {autoAlpha: 0}, 0)
            .fromTo(
              sweeps[next],
              {xPercent: -150, autoAlpha: 0},
              {
                keyframes: [
                  {autoAlpha: 0.8, duration: 0.15},
                  {xPercent: 150, autoAlpha: 0.55, duration: 0.8, ease: 'power1.inOut'},
                  {autoAlpha: 0, duration: 0.15},
                ],
              },
              0.75,
            );
        });

        // Scene i owns [i, i + 1) of the scroll, in scenes.
        const sceneAt = (progress) => {
          const position = progress * scenes.length;
          let next = Math.min(last, Math.max(0, Math.floor(position)));
          if (next === current + 1 && position - next < SWITCH_MARGIN) next = current;
          if (next === current - 1 && current - position < SWITCH_MARGIN) next = current;
          return next;
        };

        const stage = ScrollTrigger.create({
          trigger: this,
          start: 'top top',
          end: 'bottom bottom',
          onUpdate: ({progress}) => {
            if (ready) context.goTo(sceneAt(progress));
          },
        });
        show(Math.min(last, Math.floor(stage.progress * scenes.length)));
        ready = true;

        // The drift: each bottle rises a little through its own share of the
        // scroll and the sky breathes across the whole story. Smoothed, so a
        // fast flick glides instead of snapping.
        const drift = gsap.timeline({defaults: {ease: 'none'}});
        bottleStages.forEach((bottleStage, index) => {
          drift.fromTo(
            bottleStage,
            {y: phone ? 8 : 14, scale: 1},
            {y: phone ? -8 : -14, scale: phone ? 1.03 : 1.05, duration: 1},
            index,
          );
        });
        if (atmosphere) {
          drift.fromTo(atmosphere, {yPercent: 1, scale: 1.04}, {yPercent: -1, scale: 1.08, duration: scenes.length}, 0);
        }
        const driftTrigger = ScrollTrigger.create({
          animation: drift,
          trigger: this,
          start: 'top top',
          end: 'bottom bottom',
          scrub: phone ? 1 : 0.8,
        });

        const stopMotifs = phone ? () => {} : driftMotifs();

        return () => {
          stopMotifs();
          transition?.kill();
          stage.kill();
          driftTrigger.kill();
          drift.kill();
        };
      },
    );

    // The heading uses a swap font, so its metrics land after the trigger
    // measured; refresh once fonts are in.
    const refresh = () => {
      if (!this.cancelled) ScrollTrigger.refresh();
    };
    this.refreshFrame = requestAnimationFrame(refresh);
    document.fonts?.ready.then(refresh).catch(() => undefined);
  }
}

if (!customElements.get('ziva-scent-story')) {
  customElements.define('ziva-scent-story', ZivaScentStory);
}
